import { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";

/**
 * The visibility model the grid renders: the saved one, with `forced` columns
 * shown whatever it says. Returns the input unchanged when nothing is forced
 * or every forced column is already visible, so memoized references hold.
 */
export function withForcedVisibleColumns(
  model: GridColumnVisibilityModel,
  forced: readonly string[]
): GridColumnVisibilityModel {
  const hidden = forced.filter((field) => model[field] === false);
  if (hidden.length === 0) return model;
  return { ...model, ...Object.fromEntries(hidden.map((field) => [field, true])) };
}

/**
 * The model to save after a visibility change made while columns are forced.
 *
 * The grid reports forced columns as visible, so saving its model verbatim
 * would make the override permanent. Each forced column keeps the value saved
 * before the change instead, and the user's own choice returns once the
 * column stops being forced.
 */
export function withoutForcedVisibleColumns(
  next: GridColumnVisibilityModel,
  saved: GridColumnVisibilityModel,
  forced: readonly string[]
): GridColumnVisibilityModel {
  if (forced.length === 0) return next;
  const restored = { ...next };
  for (const field of forced) {
    if (field in saved) {
      restored[field] = saved[field];
    } else {
      delete restored[field];
    }
  }
  return restored;
}
